import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Package, ClipboardList, Clock, Printer, Check } from 'lucide-react';
import { ProducerLayout } from '@/components/producer';
import { useProducerById } from '@/hooks/useMyProducers';
import {
  usePickupWindows,
  usePackList,
  useWindowOrderCounts,
  useMarkPickedUp,
  formatPickupWindowLabel,
  type PickupWindow,
} from '@/hooks/usePickupWindows';
import { useMarkOrderPaid } from '@/hooks/useProducerOrders';
import { ProductThumb } from '@/components/shop/ProductThumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const PAYMENT_METHODS = [
  { value: 'swish', label: 'Swish' },
  { value: 'cash', label: 'Kontant' },
  { value: 'invoice', label: 'Faktura' },
  { value: 'card', label: 'Kort' },
  { value: 'other', label: 'Annat' },
];

/** Format short day label for tab: "Lör 14 mar" */
function formatShortDay(w: PickupWindow) {
  const d = new Date(w.pickup_date + 'T00:00:00');
  const dayName = d.toLocaleDateString('sv-SE', { weekday: 'short' });
  const dateStr = d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
  return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dateStr}`;
}

function formatTimeSpan(w: PickupWindow) {
  return `${w.pickup_start.slice(0, 5)}–${w.pickup_end.slice(0, 5)}`;
}

export default function ProducerPackListPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  const { data: allWindows = [], isLoading: windowsLoading } = usePickupWindows(farmId);
  const [selectedWindowId, setSelectedWindowId] = useState<string>('');
  const markPickedUp = useMarkPickedUp();
  const markPaid = useMarkOrderPaid();

  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentOrderId, setPaymentOrderId] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('swish');

  // Filter to recent/upcoming windows
  const relevantWindows = useMemo(() => {
    return allWindows.filter(w => {
      const windowDate = new Date(w.pickup_date + 'T23:59:59');
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return windowDate >= sevenDaysAgo;
    });
  }, [allWindows]);

  const windowIds = useMemo(() => relevantWindows.map(w => w.id), [relevantWindows]);
  const { data: orderCounts = [], isLoading: countsLoading } = useWindowOrderCounts(windowIds);

  // Build map of window id → counts
  const countsMap = useMemo(() => {
    const m = new Map<string, { orders: number; items: number }>();
    for (const c of orderCounts) {
      m.set(c.pickup_window_id, { orders: c.order_count, items: c.total_items });
    }
    return m;
  }, [orderCounts]);

  // Windows that have at least one order
  const windowsWithOrders = useMemo(() => {
    return relevantWindows.filter(w => (countsMap.get(w.id)?.orders ?? 0) > 0);
  }, [relevantWindows, countsMap]);

  // Auto-select first window with orders
  const effectiveWindowId = selectedWindowId && windowsWithOrders.some(w => w.id === selectedWindowId)
    ? selectedWindowId
    : windowsWithOrders[0]?.id || '';

  const { data: packItems = [], isLoading: packLoading } = usePackList(effectiveWindowId || undefined);

  const selectedWindow = windowsWithOrders.find(w => w.id === effectiveWindowId);

  // Derived stats
  const totalUnits = packItems.reduce((s, i) => s + i.total_quantity, 0);
  const uniqueProducts = packItems.length;

  const isInitialLoading = producerLoading || windowsLoading || countsLoading;

  const handleOpenPaymentDialog = (orderId: string) => {
    setPaymentOrderId(orderId);
    setSelectedPaymentMethod('swish');
    setPaymentDialogOpen(true);
  };

  const handleConfirmPayment = () => {
    if (!paymentOrderId) return;
    markPaid.mutate(
      { orderId: paymentOrderId, method: selectedPaymentMethod },
      { onSuccess: () => setPaymentDialogOpen(false) }
    );
  };

  const handleUndoPayment = (orderId: string) => {
    markPaid.mutate({ orderId, method: '', undo: true });
  };

  if (isInitialLoading) {
    return (
      <ProducerLayout farmId={farmId || ''} producerName={producer?.name || 'Laddar...'} title="Packlista">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout farmId={farmId || ''} producerName={producer?.name} title="Packlista">
      <div className="space-y-6 print:space-y-4" data-testid="producer-pack-list-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground print:text-xl">Packlista</h1>
            <p className="text-muted-foreground mt-1 print:text-sm">
              Välj upphämtningsdag för att se vad som ska packas
            </p>
          </div>
          {packItems.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 shrink-0 print:hidden"
              onClick={() => window.print()}
            >
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">Skriv ut</span>
            </Button>
          )}
        </div>

        {/* No windows at all */}
        {relevantWindows.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <ClipboardList className="h-8 w-8 text-muted-foreground mb-3" />
              <h3 className="text-lg font-medium text-foreground">Inga upphämtningsfönster</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                Skapa upphämtningsfönster under "Upphämtning" för att se packlistor här.
              </p>
              <Button asChild variant="outline" size="sm" className="mt-4">
                <Link to={`/app/producer/farm/${farmId}/shop/pickup-windows`}>
                  Gå till Upphämtning
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Windows exist but none have orders */}
        {relevantWindows.length > 0 && windowsWithOrders.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-8 w-8 text-muted-foreground mb-3" />
              <h3 className="text-lg font-medium text-foreground">Inga beställningar att packa</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                Det finns upphämtningsfönster men inga beställningar har kommit in ännu.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Day tabs + pack list */}
        {windowsWithOrders.length > 0 && (
          <>
            {/* Day picker tabs */}
            <div className="print:hidden">
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
                {windowsWithOrders.map(w => {
                  const isActive = w.id === effectiveWindowId;
                  const counts = countsMap.get(w.id);
                  return (
                    <button
                      key={w.id}
                      onClick={() => setSelectedWindowId(w.id)}
                      data-testid={`producer-pack-list-window-tab-${w.id}`}
                      className={cn(
                        'flex flex-col items-start gap-0.5 rounded-lg border px-4 py-3 text-left transition-colors shrink-0 min-w-[140px]',
                        isActive
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-border bg-card hover:bg-muted/50'
                      )}
                    >
                      <span className={cn(
                        'text-sm font-medium',
                        isActive ? 'text-primary' : 'text-foreground'
                      )}>
                        {formatShortDay(w)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTimeSpan(w)}
                      </span>
                      {counts && (
                        <Badge
                          variant={isActive ? 'default' : 'secondary'}
                          className="mt-1 text-xs px-1.5 py-0"
                        >
                          {counts.items} {counts.items === 1 ? 'enhet' : 'enheter'}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected window summary (visible on screen + print) */}
            {selectedWindow && (
              <div className="rounded-lg border border-border bg-muted/30 p-4 print:border-black print:bg-white">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Clock className="h-4 w-4 text-muted-foreground print:hidden" />
                  {formatPickupWindowLabel(selectedWindow)}
                </div>
                {!packLoading && packItems.length > 0 && (
                  <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                    <span>{uniqueProducts} {uniqueProducts === 1 ? 'produkt' : 'produkter'}</span>
                    <span>·</span>
                    <span>{totalUnits} {totalUnits === 1 ? 'enhet' : 'enheter'} totalt</span>
                  </div>
                )}
              </div>
            )}

            {/* Loading pack list */}
            {packLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Empty – no items for selected window */}
            {!packLoading && packItems.length === 0 && effectiveWindowId && (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-10 text-center">
                  <Package className="h-8 w-8 text-muted-foreground mb-3" />
                  <h3 className="text-base font-medium text-foreground">Inga beställningar ännu</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                    Det har inte kommit in några beställningar för detta upphämtningstillfälle.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Pack list items */}
            {!packLoading && packItems.length > 0 && (
              <div className="space-y-2 print:space-y-1">
                {packItems.map((item, idx) => (
                  <div
                    key={item.product_id}
                    className="rounded-lg border border-border bg-card print:rounded-none print:border-b print:border-x-0 print:border-t-0"
                    data-testid={`producer-pack-list-product-${item.product_id}`}
                  >
                    <div className="flex items-center gap-3 p-3 print:px-0 print:py-2">
                      <span className="hidden print:inline text-sm text-muted-foreground w-5 shrink-0 tabular-nums">
                        {idx + 1}.
                      </span>
                      <div className="print:hidden">
                        <ProductThumb
                          src={item.preview_image_url ?? undefined}
                          title={item.product_title}
                          size="md"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-foreground truncate">
                          {item.product_title}
                        </p>
                      </div>
                      <Badge
                        variant="secondary"
                        className="text-lg font-bold tabular-nums px-3 py-1 shrink-0 print:bg-transparent print:border print:border-foreground"
                      >
                        {item.total_quantity}
                      </Badge>
                    </div>

                    {/* Customer breakdown */}
                    {item.customers.length > 0 && (
                      <div className="border-t border-border px-3 pb-2 pt-1.5 print:px-0 print:pl-7">
                        {item.customers.map((c, ci) => {
                          const isPickedUp = !!c.picked_up_at;
                          const isPaid = !!c.paid_at;
                          const paidMethodLabel = c.paid_method
                            ? PAYMENT_METHODS.find(m => m.value === c.paid_method)?.label || c.paid_method
                            : '';
                          return (
                            <div
                              key={`${c.order_id}-${ci}`}
                              className={cn(
                                'flex items-center justify-between gap-2 py-1.5 text-sm',
                                isPickedUp
                                  ? 'text-muted-foreground/50'
                                  : 'text-muted-foreground'
                              )}
                            >
                              <span className={cn('truncate min-w-0 flex items-center gap-1.5', isPickedUp && 'line-through')}>
                                {c.customer_label}
                                {c.order_number && (
                                  <span className="text-xs opacity-60">#{c.order_number}</span>
                                )}
                              </span>
                              <span className="flex items-center gap-2 shrink-0 ml-3">
                                <span className={cn('tabular-nums font-medium', isPickedUp ? 'text-muted-foreground/50' : 'text-foreground')}>
                                  {c.quantity}
                                </span>

                                {/* Payment status */}
                                {isPaid ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground print:hidden">
                                    <span className="inline-flex items-center gap-0.5">
                                      <Check className="h-3.5 w-3.5" />
                                      Betald{paidMethodLabel ? ` (${paidMethodLabel})` : ''}
                                    </span>
                                    <button
                                      type="button"
                                      className="underline underline-offset-2 opacity-60 hover:opacity-100 transition-opacity"
                                      disabled={markPaid.isPending}
                                      onClick={() => handleUndoPayment(c.order_id)}
                                    >
                                      Ångra
                                    </button>
                                  </span>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs print:hidden"
                                    disabled={markPaid.isPending}
                                    onClick={() => handleOpenPaymentDialog(c.order_id)}
                                    data-testid={`producer-pack-list-mark-paid-${c.order_id}`}
                                  >
                                    Markera betald
                                  </Button>
                                )}

                                {/* Pickup status */}
                                {isPickedUp ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground print:hidden">
                                    <span className="inline-flex items-center gap-0.5">
                                      <Check className="h-3.5 w-3.5" />
                                      Hämtad
                                    </span>
                                    <button
                                      type="button"
                                      className="underline underline-offset-2 opacity-60 hover:opacity-100 transition-opacity"
                                      disabled={markPickedUp.isPending}
                                      onClick={() => markPickedUp.mutate({ orderId: c.order_id, undo: true })}
                                    >
                                      Ångra
                                    </button>
                                  </span>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs print:hidden"
                                    disabled={markPickedUp.isPending}
                                    onClick={() => markPickedUp.mutate({ orderId: c.order_id })}
                                    data-testid={`producer-pack-list-mark-picked-up-${c.order_id}`}
                                  >
                                    Markera hämtad
                                  </Button>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex justify-between items-center pt-2 border-t border-border text-sm text-muted-foreground print:pt-1" data-testid="producer-pack-list-total-units">
                  <span>{uniqueProducts} {uniqueProducts === 1 ? 'produkt' : 'produkter'}</span>
                  <span className="font-medium text-foreground">{totalUnits} enheter totalt</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment method dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Markera som betald</DialogTitle>
            <DialogDescription>Välj betalmetod som kunden använde.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedPaymentMethod} onValueChange={setSelectedPaymentMethod}>
              <SelectTrigger><SelectValue placeholder="Välj betalmetod" /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Avbryt</Button>
            <Button onClick={handleConfirmPayment} disabled={markPaid.isPending}>
              {markPaid.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Bekräfta betalning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ProducerLayout>
  );
}
